import React, { type FocusEvent, useCallback } from 'react';
import { useInlineEditReadViewContainer } from '@atlassian/jira-inline-edit-lite/src/ui/use-inline-edit-read-view-container/index.tsx';
import { useBespokeEmptyFieldTextExperiment } from '@atlassian/jira-issue-experiments/src/bespoke-empty-field-text-experiment/index.tsx';
import InlineEditReadViewContainer from '@atlassian/jira-issue-field-inline-edit-read-view-container/src/ui/index.tsx';
import { useCopyPasteTarget } from '@atlassian/jira-issue-field-copy-paste/src/ui/index.tsx';
import type { Props } from './types.tsx';

/**
 * Responsible for rendering the common read view UI elements for an issue field, including the container and associated
 * edit button.
 */
export const ReadView = ({
	children,
	context,
	editButtonLabel,
	editButtonRef,
	hideEditButton,
	isEditInteractionEnabled,
	isInvalid,
	isLoading,
	readViewContainerRef,
	readViewFitContainerHeight,
	readViewContainerTarget,
	onCopy,
	onCut,
	onPaste,
	hasReadViewTruncateDisabled,
	value,
}: Props): React.JSX.Element => {
	const { isEditButtonFocused, onClick, onMouseDown, onEditButtonBlur, onEditButtonFocus } =
		useInlineEditReadViewContainer(context, isEditInteractionEnabled);
	const { isExperimentEnabled } = useBespokeEmptyFieldTextExperiment();

	const {
		onFocus,
		onBlur,
		lastCopiedTarget: isCopied,
	} = useCopyPasteTarget({
		onCopy,
		onCut,
		// Don't allow paste interaction for non-editable fields
		onPaste: isEditInteractionEnabled ? onPaste : undefined,
	});
	const handleEditButtonBlur = useCallback(
		(e: FocusEvent<HTMLButtonElement>) => {
			onEditButtonBlur(e);
			onBlur(e);
		},
		[onEditButtonBlur, onBlur],
	);
	const handleEditButtonFocus = useCallback(
		(e: FocusEvent<HTMLButtonElement>) => {
			onEditButtonFocus(e);
			onFocus(e);
		},
		[onEditButtonFocus, onFocus],
	);

	if (readViewContainerTarget) {
		const Target = readViewContainerTarget;
		return (
			<Target
				context={context}
				editButtonLabel={editButtonLabel}
				editButtonRef={editButtonRef}
				hideEditButton={hideEditButton}
				isEditable={isEditInteractionEnabled}
				isLoading={isLoading}
				isInvalid={isInvalid}
				onClick={onClick}
				editViewPreloadTriggerRef={readViewContainerRef}
			>
				{children}
			</Target>
		);
	}

	return (
		<InlineEditReadViewContainer
			context={context}
			editButtonLabel={editButtonLabel}
			editButtonRef={editButtonRef}
			hideEditButton={hideEditButton}
			isEditInteractionEnabled={isEditInteractionEnabled}
			isEditButtonFocused={isEditButtonFocused}
			isCopied={isCopied}
			// Highlight the field as invalid to notify the user if there was an error attempting to submit changes.
			isInvalid={isInvalid}
			isLoading={isLoading}
			onClick={onClick}
			onMouseDown={onMouseDown}
			onEditButtonBlur={handleEditButtonBlur}
			onEditButtonFocus={handleEditButtonFocus}
			readViewFitContainerHeight={readViewFitContainerHeight}
			ref={readViewContainerRef}
			hasReadViewTruncateDisabled={hasReadViewTruncateDisabled}
			{...(isExperimentEnabled && { value })}
		>
			{children}
		</InlineEditReadViewContainer>
	);
};
